import { CABINET_API_URL } from '../config';
import store from '../store';
import appInsights from './appInsights';

const selectBearer = state => {
  try {
    return state.oidc.user.access_token;
  } catch (e) {
    return null;
  }
};

const getHeaders = () => ({
  Authorization: `Bearer ${selectBearer(store.getState())}`,
  Accept: 'application/json',
  'Content-Type': 'application/json',
});

const sendCommand = (deviceCode, command, params = {}) =>
  fetch(`${CABINET_API_URL}/v1/command`, {
    headers: getHeaders(),
    method: 'POST',
    body: JSON.stringify({
      deviceCode,
      command,
      params,
    }),
  }).then(response => {
    if (response.ok) {
      return response;
    } else {
      const error = new Error(response.statusText);
      error.response = response;
      appInsights.trackException({ exception: error });
      return response;
    }
  });

/**
 * Fetches the user profile from the Smart Cabinet Root API.
 * GET /v4/me (same response shape as the retired core API)
 */
export function fetchProfile() {
  return fetch(`${CABINET_API_URL}/v4/me`, {
    headers: getHeaders(),
  }).then(response => {
    if (response.ok) {
      return Promise.resolve(response.json());
    } else {
      const error = new Error(response.statusText);
      error.response = response;
      appInsights.trackException({ exception: error });
      return Promise.reject(error);
    }
  });
}

export const fetchCabinet = deviceCode => {
  return fetch(`${CABINET_API_URL}/v1/cabinets/${deviceCode}`, {
    headers: getHeaders(),
  }).then(response => {
    if (response.ok) {
      return Promise.resolve(response.json());
    } else {
      const error = new Error(response.statusText);
      error.response = response;
      appInsights.trackException({ exception: error });
      return Promise.reject(error);
    }
  });
};

/**
 * API to fetch all the products for the given user
 * @param {} group
 * @returns
 */
export const fetchProducts = group => {
  return fetch(`${CABINET_API_URL}/v1/products?group=${group}`, {
    headers: getHeaders(),
  }).then(response => {
    if (response.ok) {
      return Promise.resolve(response.json());
    } else {
      const error = new Error(response.statusText);
      error.response = response;
      appInsights.trackException({ exception: error });
      return Promise.reject(error);
    }
  });
};

export const startInventoryCorrection = (
  deviceCode,
  group,
  shelf,
  slot,
  productId,
  amount
) => {
  return fetch(
    `${CABINET_API_URL}/v3/inventory-correction/${deviceCode}?group=${group}`,
    {
      headers: getHeaders(),
      method: 'PUT',
      body: JSON.stringify({
        shelfId: shelf.toString(),
        slotId: slot.toString(),
        productData: [
          {
            id: productId,
            amount,
          },
        ],
      }),
    }
  ).then(response => {
    if (response.ok) {
      return response;
    } else {
      const error = new Error(response.statusText);
      error.response = response;
      appInsights.trackException({ exception: error });
      return response;
    }
  });
};
/**
 * Send start replenishment command to cabinet
 */
export const replenishmentStart = deviceCode =>
  sendCommand(deviceCode, 'replenishmentStart');

/**
 * Send end replenishment command to cabinet
 */
export const replenishmentEnd = deviceCode =>
  sendCommand(deviceCode, 'replenishmentEnd');

/**
 * fetch the cabinet settings (GraphQL on the Root API; the old core API is no longer called)
 * @param {*} deviceCode
 * @returns
 */
export const fetchSettings = deviceCode => {
  const query = `{device(code: "${deviceCode}") { alternativeIds } }`;

  return fetch(`${CABINET_API_URL}/v4/graphql`, {
    headers: getHeaders(),
    method: 'POST',
    body: JSON.stringify({ query }),
  }).then(response => {
    if (response.ok) {
      return Promise.resolve(response.json());
    } else {
      const error = new Error(response.statusText);
      error.response = response;
      appInsights.trackException({ exception: error });
      return Promise.reject(error);
    }
  });
};

/**
 * update the cabinet refill filter setting.
 * The Root API merges the given keys into the cabinet settings and answers { code, settings }.
 * @param {*} deviceCode
 * @param {*} filter
 * @returns
 */
export const updateSetting = (deviceCode, filter) => {
  return fetch(`${CABINET_API_URL}/v3/device-settings/${deviceCode}`, {
    headers: getHeaders(),
    method: 'PATCH',
    body: JSON.stringify({ refillFilter: filter }),
  }).then(response => {
    if (response.ok) {
      return Promise.resolve(response.json());
    } else {
      const error = new Error(response.statusText);
      error.response = response;
      appInsights.trackException({ exception: error });
      return Promise.reject(error);
    }
  });
};

export const notifyCabinetSettingUpdate = deviceCode => {
  sendCommand(deviceCode, 'notifyCabinetToFetchDeviceConfiguration');
};

export const fetchReplenishmentPlan = (group, deviceCode) => {
  return fetch(
    `${CABINET_API_URL}/v3/refill-plans/${deviceCode}?group=${group}`,
    {
      headers: getHeaders(),
    }
  ).then(response => {
    if (response.ok) {
      return Promise.resolve(response.json());
    } else {
      let error = new Error(response.statusText);
      error.response = response;
      appInsights.trackException({ exception: error });

      if (response.status === 403) {
        error = 'error:forbidden';
      } else if (response.status === 404) {
        error = 'error:not_found_plan';
      } else {
        error = 'error:unknown';
      }
      throw error;
    }
  });
};

export const fetchReplenishmentRule = (group, deviceCode) => {
  return fetch(
    `${CABINET_API_URL}/v3/refill-rules/${deviceCode}?group=${group}`,
    {
      headers: getHeaders(),
    }
  ).then(response => {
    if (response.ok) {
      return Promise.resolve(response.json());
    } else {
      let error = new Error(response.statusText);
      error.response = response;
      appInsights.trackException({ exception: error });

      if (response.status === 403) {
        error = 'error:forbidden';
      } else if (response.status === 404) {
        error = 'error:not_found_plan';
      } else {
        error = 'error:unknown';
      }
      throw error;
    }
  });
};

export const updateReplenishmentPlanStatus = (
  group,
  replenishPlanId,
  status,
  refillOperator
) => {
  return fetch(
    `${CABINET_API_URL}/v3/refill-plans/${replenishPlanId}?group=${encodeURIComponent(
      group
    )}&status=${status}&refillOperator=${encodeURIComponent(refillOperator)}`,
    {
      headers: getHeaders(),
      method: 'PATCH',
    }
  ).then(response => {
    if (!response.ok) {
      let error = new Error(response.statusText);
      error.response = response;
      appInsights.trackException({ exception: error });

      error = 'error:plan_status';
      throw error;
    }
  });
};

export const fetchCabinetInventory = cabinetCode => {
  const url = new URL(`${CABINET_API_URL}/v1/inventory/${cabinetCode}`);

  return fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  }).then(response => {
    if (response.status === 204) {
      return Promise.resolve({
        readTime: null,
        tags: [],
      });
    }

    if (response.ok) {
      return Promise.resolve(response.json());
    } else {
      let error = new Error(response.statusText);
      error.response = response;
      appInsights.trackException({ exception: error });

      error = 'error:fetch_cabinet_inventory';
      throw error;
    }
  });
};

export const fetchCabinetStatus = (cabinetCode, group) => {
  const url = new URL(
    `${CABINET_API_URL}/v3/cabinet-state/${cabinetCode}?group=${group}`
  );

  return fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  }).then(response => response.json());
};

export const fetchPlanogram = (deviceCode, group) => {
  const url = new URL(
    `${CABINET_API_URL}/v3/planogram/${deviceCode}?group=${group}`
  );
  return fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  }).then(response => {
    if (response.ok) {
      appInsights.trackTrace({
        message: 'Refill plan fetched',
      });
      return Promise.resolve(response.json());
    } else {
      const error = new Error(response.statusText);
      error.response = response;
      appInsights.trackException({ exception: error });
      return Promise.reject(error);
    }
  });
};

/**
 * Passes the parameters to the sendCommand method and returns its response.
 * @param {*} deviceCode Device code
 * @param {*} command command
 * @param {*} params extra parameters
 * @returns Returns the response from the sendCommand method.
 */
export const sendCalibrationCommand = (deviceCode, command, params) =>
  sendCommand(deviceCode, command, params);
